<template>
    <div class="w-full">

        <!--WALLET-->
        <div class="w-full flex flex-column gap-2 mb-3">
            <span class="text-600 text-sm">{{ content[lang].from }}:</span>
            <div v-if="wallets && !loading" class="p-inputgroup w-full flex flex-row">
                <Select v-model="wallet" :options="walletList" :disabled="freeze"
                    option-label="get" class='w-full border-2 border-round-xl' size="large" :placeholder="content[lang].from">
                    <template #value="slotProps">
                        <div v-if="slotProps.value" class="flex align-items-center h-full py-2 text-sm">
                            {{ slotProps.value.name }} <span class="font-medium ml-1">{{ getFormatAmount(slotProps.value.balance, 'usdt') }}</span>
                        </div>
                        <span v-else class="flex align-items-center h-full py-2 text-sm">
                            {{ slotProps.placeholder }}
                        </span>
                    </template>
                    <template #option="slotProps">
                        <div class="flex align-items-center w-7rem">
                            {{ slotProps.option.name }} <span class="font-medium ml-1">{{ getFormatAmount(slotProps.option.balance, 'usdt') }}</span>
                        </div>
                    </template>
                </Select>
            </div>
            <Skeleton v-else height="3rem" class="border-round-lg" />
        </div>

        <!--ACCOUNT-->
        <div class="w-full flex flex-column gap-2 mb-3">
            <span class="text-600 text-sm">{{ content[lang].currency }}:</span>
            <div v-if="accountList && !loading" class="p-inputgroup w-full flex flex-row">
                <Select v-model="account" :options="accountList"
                    option-label="get" class='w-full border-2 border-round-xl' size="large" :placeholder="content[lang].currency">
                    <template #value="slotProps">
                        <div class="flex flex-row w-full align-items-center gap-2">
                            <AvatarGroup v-if="slotProps.value && slotProps.value.currency" :max="2" class="gap-1">
                                <Avatar :image="getCurImage(slotProps.value.currency)" class="surface-0" shape="circle"/>
                                <Avatar v-if="slotProps.value.network" :image="getNetworkImage(slotProps.value.network)" class="surface-0" shape="circle"/>
                            </AvatarGroup>
                            <div class="flex align-items-center h-full py-2 text-sm">
                                <div v-if="slotProps.value"><span class="font-medium ml-1">{{ getFormatAmount(slotProps.value.balance, slotProps.value.currency) }}</span></div>
                                <div v-else>{{ slotProps.placeholder }}</div>
                            </div>
                        </div>
                    </template>
                    <template #option="slotProps">
                        <div class="flex flex-row w-full align-items-center gap-2">
                            <AvatarGroup v-if="slotProps.option && slotProps.option.currency" :max="2" class="gap-1">
                                <Avatar :image="getCurImage(slotProps.option.currency)" class="surface-0" shape="circle"/>
                                <Avatar v-if="slotProps.option.network" :image="getNetworkImage(slotProps.option.network)" class="surface-0" shape="circle"/>
                            </AvatarGroup>
                            <div class="flex flex-1 text-sm">
                                <span class="font-medium ml-1">{{ getFormatAmount(slotProps.option.balance, slotProps.option.currency) }}</span>
                            </div>
                        </div>
                    </template>
                </Select>
            </div>
            <Skeleton v-else height="3rem" class="border-round-lg" />
        </div>

        <!--AMOUNT-->
        <div class="flex flex-column gap-2 mb-3">
            <span class="flex text-600 text-sm">{{ content[lang].sum_title }}:</span>
            <div v-if="!loading" class="flex flex-column w-full">
                <div class="flex flex-row p-inputgroup w-full">
                    <InputNumber v-model="withdrawAmount" id="withdrawAmount" class="flex w-full border-2 border-round-xl surface-border overflow-hidden amount_input" :useGrouping="false" size="large"
                        :min="minAmount" :max="maxAmount" :disabled="!currency || amountDisabled" :maxFractionDigits="2" :placeholder="content[lang].sum"/>
                </div>
                <span v-if="minAmount < maxAmount" class="flex flex-row text-500 text-sm mt-2 gap-1">
                    <span class="flex text-primary cursor-pointer" @click="setMin()">
                        min
                    </span>
                    <span class="flex">
                        {{ minAmount }} - {{ maxAmount }}
                    </span>
                    <span class="flex text-primary cursor-pointer" @click="setMax()">
                        max
                    </span>
                </span>
                <span v-else class="flex flex-row text-500 text-sm mt-2">
                    {{ content[lang].not_enough }}
                </span>
            </div>
            <Skeleton v-else height="3rem" class="border-round-lg" />
        </div>

        <!--RECIPIENTS-->
        <div v-if="recipientChoosen && currency" class="flex flex-column w-full mb-3 gap-2">
            <span class="flex text-600 text-sm">{{ content[lang].recipients }}:</span>
            <div @click="popupRecipients = true"
                class="input_banks flex flex-row cursor-pointer relative w-full py-3 px-2 mb-3 surface-border border-2 border-round-xl justify-content-left align-items-center overflow-hidden gap-2">
                <div class="flex">
                    <AvatarGroup v-if="recipientChoosen.currency_code" :max="2" class="gap-1">
                        <Avatar :image="getCurImage(recipientChoosen.currency_code)" class="surface-0" shape="circle"/>
                        <Avatar v-if="recipientChoosen.fields && recipientChoosen.fields.wallet_network" 
                            :image="getNetworkImage(recipientChoosen.fields.wallet_network)" class="surface-0" shape="circle"/>
                    </AvatarGroup>
                </div>
                <div class="flex flex-1 flex-row text-sm relative justify-content-left align-items-center">
                    <span class="flex flex-column">
                        <span class="flex text-700 font-medium white-space-nowrap mr-2">
                            {{ recipientChoosen.fields.fullname }}
                        </span>
                        <span class="flex text-500 text-xs white-space-nowrap" style="margin-top:3px">
                            {{ getBankTitle(recipientChoosen.bank_code) }}
                        </span>
                        <span class="flex text-500 text-xs white-space-nowrap" style="margin-top:3px">
                            {{ (recipientChoosen.fields && recipientChoosen.fields.wallet_address) ? getShortAddress(recipientChoosen.fields.wallet_address) : '' }}
                        </span>
                    </span>
                    <div class="absolute right-0 mr-3 surface-0 p-2 border-circle">
                        <i class="pi pi-verified text-600 ml-2 text-primary"></i>
                    </div>
                </div>
                <i class="pi pi-chevron-down text-600 absolute right-0 mr-0 surface-0 p-2 border-circle"></i>
            </div>
        </div>

        <Dialog v-if="filteredRecipients" v-model:visible="popupRecipients" modal :header="content[lang].recipient" 
            class="w-full ml-2 mr-2 border-round-xl overflow-hidden" style="max-width: 500px;">
            <div v-for="item of filteredRecipients">
                <div class="flex flex-row cursor-pointer relative px-2 mb-3 surface-border border-2 border-round-xl justify-content-left align-items-center input_banks overflow-hidden gap-2"
                    @click="chooseRecipient(item._id)">
                    <div class="flex">
                        <AvatarGroup v-if="item.currency_code" :max="2" class="gap-1">
                            <Avatar :image="getCurImage(item.currency_code)" class="surface-0" shape="circle"/>
                            <Avatar v-if="item.fields.wallet_network" 
                                :image="getNetworkImage(item.fields.wallet_network)" class="surface-0" shape="circle"/>
                        </AvatarGroup>
                    </div>
                    <span class="flex flex-1 flex-column text-sm">
                        <span class="flex text-700 font-medium white-space-nowrap mr-2">
                            {{ item.fields.fullname }}
                        </span>
                        <span class="flex text-500 text-xs white-space-nowrap" style="margin-top:3px">
                            {{ getBankTitle(item.bank_code) }} 
                        </span>
                        <span class="flex text-500 text-xs white-space-nowrap" style="margin-top:3px">
                            {{ (item.fields && item.fields.wallet_address) ? getShortAddress(item.fields.wallet_address) : '' }}
                        </span>
                    </span>    
                    <div class="absolute right-0 mr-0 surface-0 p-2 border-circle">
                        <i class="pi pi-verified text-600 ml-2 text-primary"></i>
                    </div>                
                </div>
            </div>
        </Dialog>

        <!--RECIPIENT FIELDS-->
        <div v-if="!loading && !recipientChoosen && currency">
            <RecipientFields @check_valid="setValidReceipent" @return_fields="setFields" 
                :fields="['fullname','wallet_address', 'wallet_network']" 
                :requiredFields="['fullname', 'wallet_address', 'wallet_network']" 
                :allowedNetworks="allowedNetworks" 
                :currency="currency"/>
        </div>

        <!--ADD RECIPIENT-->
        <div v-if="!loading && recipientChoosen && currency" class="w-full flex flex-row gap-3 justify-content-between align-items-center mb-3">
            <span class="cursor-pointer text-primary text-sm" @click="newRecipient(true)">
                <span class="mr-2">{{ content[lang].create_recipient }}</span>
                <i class="pi pi-user-plus text-600 mr-2 text-primary"></i>
            </span>
            <span class="cursor-pointer text-primary text-sm" @click="goToRecipients()">
                <span class="mr-2">{{ content[lang].all_recipients }}</span>
                <i class="pi pi-external-link text-600 mr-2 text-primary"></i>
            </span>
        </div>

        <div v-if="!loading && !recipientChoosen && recipients && recipients.length>0" class="w-full">
            <span class="cursor-pointer text-primary text-sm" @click="newRecipient(false)">
                <span class="mr-2">{{ content[lang].select_recipient }}</span>
                <i class="pi pi-users text-600 mr-2 text-primary"></i>
            </span>
        </div>
    </div>
</template>


<script>
import Connect from '@/assets/js/Connect'
import Helper from '@/assets/js/Helper'
import BigNumber from 'bignumber.js'
import RecipientFields from '@/components/widgets/RecipientFields.vue'
import { mapActions } from  'vuex'
import wallet from '@/store/modules/wallet'

export default {
    props: {
        wallet_id: {
            type: Number,
            default: null
        },
        onClick: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            loading: false,
            currencies: [],
            minWithdraw: 10,
            withdrawAmount: 0,
            amountDisabled: false,
            allowCurrencies: ['usdt_bsc', 'usdt_trc'],
            allowedNetworks: [],
            freeze: false,
            wallets: [],
            wallet: null,
            walletList: [],
            account: null,
            accountList: [],
            currency: null,
            recipientChoosed: false,
            recipientChoosen: null,
            recipients: [],
            filteredRecipients: [],
            popupRecipients: false,
            hideRecipients: false,
            recipientFieldsValid: false,
            recipientFields: {},

            content: {
                ru: {
                    from: 'Со счета',
                    currency: 'Валюта',
                    sum: 'Сумма',
                    sum_title: 'Укажите сумму вывода',
                    address_title: 'Укажите адрес вывода',
                    recipients: 'Получатели',
                    recipient: 'Получатель',
                    create_recipient: 'Создать получателя',
                    all_recipients: 'Все получатели',
                    select_recipient: 'Выбрать получателя',
                    btn: 'Вывести',
                    success: 'Успех',
                    sent: 'Заявка на вывод отправлена',
                    error: 'Ошибка',
                    notfound: 'Кошелек не найден',
                    notsent: 'Ошибка отправки заявки на вывод',
                    not_enough: 'Недостаточно средств для вывода'
                },
                en: {
                    from: 'From wallet',
                    currency: 'Currency',
                    sum: 'Amount',
                    sum_title: 'Enter the withdrawal amount',
                    recipients: 'Recipients',
                    recipient: 'Recipient',
                    create_recipient: 'Create recipient',
                    all_recipients: 'All recipients',
                    select_recipient: 'Select recipient',
                    address_title: 'Enter the withdrawal address',
                    btn: 'Withdraw',
                    success: 'Success',
                    sent: 'Withdrawal request sent',
                    error: 'Error',
                    notfound: 'Wallet not found',
                    notsent: 'Error sending withdrawal request',
                    not_enough: 'Not enough funds for withdrawal'
                }
            }
        }
    },
    components: {
        RecipientFields
    },
    computed: {
        lang() { return (this.$i18n.locale) ? this.$i18n.locale : 'ru' },
        wallets() { return this.$store.getters.StateWallets },
        canWithdraw() {
            if(!this.recipientChoosen && !this.recipientFieldsValid) return false
            if(!this.account || !this.account.balance || this.account.balance <= 0) return false
            if(!this.withdrawAmount || this.withdrawAmount <= 0) return false
            if(this.withdrawAmount < this.minWithdraw) return false
            if(this.withdrawAmount > this.getMaxSum(2)) return false
            return true
        },
    },
    watch: {
        wallets: function () {
            if(!this.wallets || !this.wallets.length || this.wallets.length <= 0) return
                
            let elList = []
            for (let i = 0; i < this.wallets.length; i++) {
                const balance = this.roundAmount(this.wallets[i].balance)
                let el = {
                    type: this.wallets[i].ext_type,
                    balance: balance,
                    name: this.getWalletNameByType(this.wallets[i].ext_type),
                    id: this.wallets[i].id,
                    accounts: this.wallets[i].accounts,
                }
                elList.push(el)
            }
            this.walletList = elList

            if(this.wallet_id) {
                this.wallet = elList.find(item => item.id == this.wallet_id)
                if(!this.wallet) {
                    this.showMess('error', this.content[this.lang].error, this.content[this.lang].notfound)
                    this.wallet = elList[0]
                } else {
                    this.freeze = true
                }
            } else {
                this.wallet = elList[0]
            }
        },
        wallet: function (val) {
            if(val && val.id) {
                this.accountList = this.getAccountsByWallet(val.id, ['usdt_bsc', 'usdt_trc'])
                this.account = (this.accountList && this.accountList.length) ? this.accountList[0] : null
            }
        },
        account: function (val) {
            if(val && val.currency) this.currency = val.currency
            let bBalance = new BigNumber(val.balance)
            if(bBalance.isNaN() || bBalance.isLessThanOrEqualTo(0)) {
                this.minAmount = 0
                this.maxAmount = 0
                this.withdrawAmount = 0
                this.amountDisabled = true
            } else {
                const amount = this.roundAmount(val.balance)
                this.minAmount = this.minWithdraw
                this.maxAmount = amount
                this.withdrawAmount = this.minAmount
                this.amountDisabled = false
            }
        },
        currency: function (val) {
            this.filterRecipients(val)
            if(this.filteredRecipients && this.filteredRecipients.length > 0) {
                this.recipientChoosen = this.filteredRecipients[0]
                this.recipientChoosed = true
            } else {
                this.recipientChoosen = null
                this.recipientChoosed = false
            }
            if(val == 'usdt_bsc') this.allowedNetworks = ['bep20']
            else if(val == 'usdt_trc') this.allowedNetworks = ['trc20']
            else this.allowedNetworks = []
        },
        withdrawAmount: function (val) {
            if(val>this.getMaxSum(2)) this.withdrawAmount = this.getMaxSum(2)
            if(val < this.minWithdraw) this.$emit('disabled', true)
        },
        onClick: function () {
            this.DoWithdraw()
        },
        loading: function(that){
            this.$emit('loading', that)
        },
        canWithdraw: function(val){
            this.$emit('disabled', !val)
        },
    },
    methods: {
        ...mapActions(['ActionGetAllWallets', 'ActionWithdrawWalletToRecipient']),
        setFields(fields){
            this.recipientFields = fields
        },
        setValidReceipent(value){
            this.recipientFieldsValid = (value) ? true : false
        },
        getWalletNameByType(type) {
            return this.helper.getWalletNameByType(type, this.lang)
        },
        getBankImage(code){
            return this.helper.get_bank_image(code, this.$store.state.payment.banks)
        },
        getBankTitle(code){
            return this.helper.get_bank_title(code, this.$store.state.payment.banks, this.lang)
        },
        roundAmount(val){
            const bAmount = new BigNumber(val)
            if(bAmount.isNaN()) return 0
            const floored = bAmount.decimalPlaces(2, BigNumber.ROUND_FLOOR)
            const decimals = floored.decimalPlaces()
            if(decimals >= 2) return floored.toFixed(2)
            else if(decimals == 1)return floored.toFixed(1)
            else return floored.toFixed(0)
        },
        async chooseRecipient(id) {
            this.popupRecipients = false
            this.recipientChoosen = this.getRecipientById(id)
            this.hideRecipients = false
        },
        getFormatAmount(value, currency, decimals = 2){
            return this.helper.format_amount(value, currency, decimals)
        },
        showMess(severity, summary, detail) {
            this.$toast.add({ severity: severity, summary: summary, detail: detail, life: 3000 });
        },
        getShortAddress(value){
            if(!value) return ''
            return this.helper.get_short_name(value, 25)
        },
        getCurImage(code){
            return this.helper.GetCurrencyImage(code, this.currencies)
        },
        getNetworkImage(network) {
            if(!network) return null
           return this.helper.GetNetworkImage(network)
        },
        newRecipient(val){
            if(val){
                this.recipientChoosen = null
                this.hideRecipients = true
            } else {
                if(this.currency) this.filterRecipients(this.currency)
                this.hideRecipients = false
            }
        },
        goToRecipients() {
            this.$router.push({ path: '/personal/recipients/' })
        },
        filterRecipients(currency_code) {
            if(!this.recipients || !this.recipients.length) return []
            let filtered = []
            this.filteredRecipients = []
            for(let i = 0; i < this.recipients.length; i++) {
                if( this.recipients[i].currency_code == currency_code && 
                    this.allowCurrencies.includes(currency_code) &&
                    this.recipients[i]?.payment_system_code && 
                    (this.recipients[i]?.payment_system_code == 'crypto_withdraw' || this.recipients[i]?.payment_system_code == 'crypto')) {
                    filtered.push(this.recipients[i])
                }
            }
            this.filteredRecipients = filtered
            this.recipientChoosen = (filtered && filtered.length) ? filtered[0] : null
            this.recipientChoosed = (filtered && filtered.length) ? true : false
        },
        getRecipientById(id) {
            if(!this.recipients || !this.recipients.length) return null
            const recipient = this.recipients.find(item => item._id == id)
            if(recipient) {
                this.recipientChoosen = recipient
                this.recipientChoosed = true
                return recipient
            } else {
                this.recipientChoosen = null
                this.recipientChoosed = false
                return null
            }
        },
        getAccountsByWallet(wallet_id, arrCurrency = null) {
            if(!this.wallets || !this.wallets.length) return []
            const accounts = this.wallets.find(item => item.id == wallet_id)
            if(accounts && accounts.account_details && accounts.account_details.length) {
                let elList = []
                for (let i = 0; i < accounts.account_details.length; i++) {
                    if(arrCurrency && !arrCurrency.includes(accounts.account_details[i].currency)) continue
                    let el = {
                        id: accounts.account_details[i].id,
                        type: accounts.account_details[i].ext_type,
                        balance: accounts.account_details[i].balance,
                        currency: accounts.account_details[i].currency,
                        network: accounts.account_details[i]?.network,
                    }
                    elList.push(el)
                }
                return elList
            } else {
                return []
            }
        },
        async getWallets(){
            this.loading = true
            try { 
                const resWallets = await this.ActionGetAllWallets().then(res=>res)
                if(resWallets.result && resWallets.data && resWallets.data.wallets) this.wallets = resWallets.data.wallets
                else this.showMess('error', this.content[this.lang].error, this.content[this.lang].error_text)
            } catch (error) { console.log(error) }
            this.loading = false
        },
        async getRecipients(){
            const res = await this.connect.user_recipients('array').then(res => res)
            if(!res || !res.result || !res.data || !Array.isArray(res.data)) return
            this.recipients = res.data
            if(this.currency) this.filterRecipients(this.currency)
            else this.filteredRecipients = []
        },
        async DoWithdraw() {
            this.loading = true
            try {
                const recipientId = (this.recipientChoosen && this.recipientChoosen._id) ? this.recipientChoosen._id : null
                const currencyCode = (this.recipientChoosen && this.recipientChoosen.currency_code) ? this.recipientChoosen.currency_code : (this.account && this.account.currency) ? this.account.currency : null
                const data = {
                    wallet_id: (this.wallet && this.wallet.id) ? this.wallet.id : null,
                    recipient_id: recipientId,
                    amount: this.withdrawAmount,
                    currency_code: currencyCode,
                    fields: this.recipientFields,
                }
                const send = await this.ActionWithdrawWalletToRecipient(data).then(res => res)
                if(send.result && send.data) {
                    this.showMess('success', this.content[this.lang].success, this.content[this.lang].sent)
                    this.ActionGetAllWallets().then(res=>res)
                    this.$emit('close', true)
                } else {
                    const err = (send.errors && send.errors.length) ? send.errors[0] : 'Ошибка вывода средств. Обратитесь в службу поддержки'
                    this.showMess('error', this.content[this.lang].error, err)
                }
            } catch (e) {
                this.showMess('error', this.content[this.lang].error, e.message)
            }
            this.loading = false
        },
        getCurrencyList(){
            const objList = this.$store.state.payment.currencies
            if(!objList)return
            let currencies = []
            for(const [k,v] of Object.entries(objList)){
                currencies.push({
                    code: v.code,
                    name: k.toUpperCase(),
                    image: v.image
                })
            }
            this.currencies = currencies
        },
        setMin(){
            this.withdrawAmount = this.minWithdraw
        },
        getMaxSum(fixed=0){
            if(!this.account || !this.account || !this.account.balance) return 0
            const balance = new BigNumber(this.account.balance)
            return balance.toFixed(fixed)
        },
        setMax(){
            this.withdrawAmount = this.getMaxSum(2)
        }
    },
    async beforeCreate() {
        this.connect = new Connect()
        this.helper = new Helper()
    },
    async mounted() {
        await this.getWallets().then(res=>res)
        await this.getRecipients().then(res=>res)
        this.getCurrencyList()
    }
}
</script>